export class cyclicValidator {
    constructor(gantt, flatDataCollection) {
        this.idToItem = new Map();
        this.parentToChildren = new Map();
        this.leafCache = new Map(); // memoize leaf descendants
        this.adjacency = new Map(); // from -> set(to)
        this.resolvedEdgesPerTask = new Map();
        this.cycles = [];
        this.topoOrder = null;
        this.parent = gantt;
        this.buildMaps(flatDataCollection);
    }
    buildMaps(flatDataCollection) {
        this.idToItem.clear();
        if (flatDataCollection) {
            flatDataCollection.clear();
        }
        this.parentToChildren.clear();
        if (this.parent.viewType === 'ProjectView') {
            for (const item of this.parent.flatData) {
                if (flatDataCollection) {
                    flatDataCollection.set(item.ganttProperties.rowUniqueID.toString(), item);
                }
                const id = String(item.ganttProperties.taskId);
                this.idToItem.set(id, item);
                // initialize adjacency/containers
                if (!this.adjacency.has(id)) {
                    this.adjacency.set(id, new Set());
                }
                // parent map
                let parent;
                if (item.parentItem && item.parentItem.taskId) {
                    parent = item.parentItem.taskId;
                }
                if (parent !== undefined && parent !== null && parent !== '') {
                    const pid = (parent);
                    if (!this.parentToChildren.has(pid)) {
                        this.parentToChildren.set(pid, []);
                    }
                    this.parentToChildren.get(pid).push(id);
                }
            }
        }
    }
    getLeafDescendants(nodeId) {
        if (this.leafCache.has(nodeId)) {
            return this.leafCache.get(nodeId);
        }
        const result = [];
        result.push(nodeId);
        const stack = [nodeId];
        while (stack.length) {
            const cur = stack.pop();
            const children = this.parentToChildren.get(cur);
            if (!children || children.length === 0) {
                result.push(cur);
            }
            else {
                for (const c of children) {
                    stack.push(c);
                }
            }
        }
        this.leafCache.set(nodeId, result);
        return result;
    }
    resolveOnePred(p) {
        const fromId = (p.from);
        const toId = (p.to);
        const fromLeaves = this.getLeafDescendants(fromId);
        const toLeaves = this.getLeafDescendants(toId);
        const edges = [];
        for (const f of fromLeaves) {
            for (const t of toLeaves) {
                edges.push({ fromLeaf: f, toLeaf: t, source: p });
            }
        }
        return edges;
    }
    /**
     * Resolve every predecessor in the dataset once.
     * Runs in O(N + E) total time complexity.
     *
     * @returns {void} Nothing is returned.
     */
    resolve() {
        this.adjacency.clear();
        this.resolvedEdgesPerTask.clear();
        this.leafCache.clear();
        this.cycles = [];
        this.topoOrder = null;
        // Ensure adjacency nodes exist
        const keys = Array.from(this.idToItem.keys());
        for (let i = 0; i < keys.length; i++) {
            const id = keys[i];
            if (!this.adjacency.has(id)) {
                this.adjacency.set(id, new Set());
            }
        }
        // For each task, expand its predecessor collection and add edges
        const iterator = this.idToItem.entries();
        let entry = iterator.next();
        while (!entry.done) {
            const [id, item] = entry.value;
            const preds = item.ganttProperties.predecessor || [];
            const resolvedList = [];
            for (let i = 0; i < preds.length; i++) {
                const p = preds[i];
                const edges = this.resolveOnePred(p);
                for (let j = 0; j < edges.length; j++) {
                    const e = edges[j];
                    if (!this.adjacency.has(e.fromLeaf)) {
                        this.adjacency.set(e.fromLeaf, new Set());
                    }
                    this.adjacency.get(e.fromLeaf).add(e.toLeaf);
                    resolvedList.push(e);
                }
            }
            this.resolvedEdgesPerTask.set(id, resolvedList);
            // Advance iterator
            entry = iterator.next();
        }
        // After adjacency built, run cycle detection once
        this.cycles = this.detectAllCycles();
        if (this.cycles.length === 0) {
            this.topoOrder = this.computeTopologicalOrder();
        }
    }
    /**
     * Returns resolved predecessor edges for a given task ID.
     *
     * @param {string | number} taskId - Task ID (string or number) to retrieve resolved predecessors for
     * @returns {{ fromLeaf: string; toLeaf: string; source: IPredecessor }[]} Array of resolved edges, empty if none
     * @private
     */
    // eslint-disable-next-line
    getResolvedPredecessorsForTask(taskId) {
        return this.resolvedEdgesPerTask.get(String(taskId)) || [];
    }
    /**
     * Creates a deep clone of the current adjacency map.
     *
     * @returns {Map<string, Set<string>>} A new Map with the same keys and independently cloned Sets
     * @private
     */
    // eslint-disable-next-line
    cloneAdjacency() {
        const m = new Map();
        const entries = this.adjacency.entries();
        let entryResult = entries.next();
        while (!entryResult.done) {
            const pair = entryResult.value;
            const k = pair[0];
            const set = pair[1];
            m.set(k, new Set(Array.from(set)));
            entryResult = entries.next();
        }
        return m;
    }
    /**
     * Creates a temporary clone of the adjacency map and adds resolved edges from the given predecessor.
     * Used by cycle detection to simulate adding a dependency without modifying the original graph.
     *
     * @param {IPredecessor} pred - The predecessor object to resolve and add
     * @returns {Map<string, Set<string>>} A new adjacency map including the resolved edges
     * @private
     */
    // eslint-disable-next-line
    addPredToAdjacencyClone(pred) {
        const clone = this.cloneAdjacency();
        // we must use existing leafCache + maps to resolve quickly
        const edges = this.resolveOnePred(pred);
        for (const e of edges) {
            if (clone && !clone.has(e.fromLeaf)) {
                clone.set(e.fromLeaf, new Set());
            }
            clone.get(e.fromLeaf).add(e.toLeaf);
        }
        return clone;
    }
    /**
     * Check if adding the given predecessor would create a cycle.
     *
     * @param {IPredecessor} pred - The predecessor to test for cycle creation.
     * @returns {CycleCheckResult} An object describing whether a cycle would be created and the cycles found.
     * @private
     */
    wouldCreateCycleWhenAdding(pred) {
        const adj = this.addPredToAdjacencyClone(pred);
        const cycles = this.detectCyclesStatic(adj);
        return {
            wouldCreate: cycles.length > 0,
            cycles
        };
    }
    detectAllCycles() {
        const cycles = this.detectCyclesStatic(this.adjacency);
        const result = [];
        for (const cycle of cycles) {
            if (cycle[0] && cycle[1]) {
                result.push({ fromId: cycle[0], toId: cycle[1] });
            }
        }
        return result;
    }
    removePredecessor(predecessorString, toRemove) {
        if (!predecessorString) {
            return '';
        }
        if (predecessorString === toRemove) {
            return '';
        }
        const parts = predecessorString
            .split(',')
            .map((s) => s.trim())
            .filter((part) => part !== toRemove && part !== toRemove.trim());
        return parts.join(',');
    }
    getCyclesWithDetails(cycles) {
        const parts = cycles.map((cycle) => {
            const fromId = cycle['fromId'];
            const toId = cycle['toId'];
            const toTask = this.idToItem.get(toId);
            const fromTask = this.idToItem.get(fromId);
            const toPredecessors = toTask.ganttProperties.predecessor;
            // Default error label
            let errorLabel = `Task ID ${fromId} to Task ID ${toId}`;
            const newPred = [];
            // STRICTLY check the 'toTask' (Successor) only.
            if (toPredecessors) {
                for (let i = 0; i < toPredecessors.length; i++) {
                    const pred = toPredecessors[i];
                    // Resolve the predecessor's ID to its leaves
                    const leaves = this.getLeafDescendants(pred.from);
                    // Check if the predecessor (or its children) matches the cycle source 'fromId'
                    if (pred.from === fromId || (leaves && leaves.indexOf(fromId) !== -1)) {
                        // Update label to show the actual Predecessor ID (e.g., "1" instead of "3")
                        errorLabel = `Task ID ${pred.from} to Task ID ${toId}`;
                        let predType = pred.from + pred.type;
                        predType = this.parent.predecessorModule['generatePredecessorValue'](pred, predType);
                        // Remove ONLY this invalid dependency from the 'toTask'
                        const dependencyField = this.parent.taskFields.dependency;
                        toTask.taskData[dependencyField] = toTask[dependencyField] =
                            toTask.ganttProperties.predecessorsName =
                                this.removePredecessor(toTask.ganttProperties.predecessorsName, predType);
                        const removedString = predType.split(',');
                        removedString.forEach((str) => {
                            const id = str.replace(/(FS|SS|SF|FF).*$/, '');
                            const targetTask = this.idToItem.get(id);
                            if (targetTask.ganttProperties.predecessor) {
                                targetTask.ganttProperties.predecessor = targetTask.ganttProperties.predecessor.filter((pred) => pred.to.toString() !== toTask.ganttProperties.taskId.toString());
                            }
                        });
                        continue;
                    }
                    else {
                        newPred.push(pred);
                    }
                }
                // Update the task with the clean list
                toTask.ganttProperties.predecessor = newPred;
            }
            return errorLabel;
        });
        // Remove duplicates from the error messages (e.g. if 3->6 and 4->6 both map to "1->6", show it once)
        const uniqueParts = parts.filter((value, index, self) => self.indexOf(value) === index);
        const list = uniqueParts.join(', ');
        return `Cyclic dependency is detected for the tasks from ${list}. Please provide valid dependency`;
    }
    detectCyclesStatic(adj) {
        const WHITE = 0;
        const GRAY = 1;
        const BLACK = 2;
        const state = new Map();
        // Initialize all nodes as WHITE
        const keysInit = Array.from(adj.keys());
        for (let i = 0; i < keysInit.length; i++) {
            const k = keysInit[i];
            state.set(k, WHITE);
        }
        const stack = [];
        const cycles = [];
        const dfs = (node) => {
            state.set(node, GRAY);
            stack.push(node);
            const neighbors = adj.get(node);
            if (neighbors) {
                const neighborArray = Array.from(neighbors);
                for (let i = 0; i < neighborArray.length; i++) {
                    const nb = neighborArray[i];
                    const s = state.has(nb) ? state.get(nb) : WHITE;
                    if (s === WHITE) {
                        dfs(nb);
                    }
                    else if (s === GRAY) {
                        // Cycle detected
                        let idx = -1;
                        for (let j = 0; j < stack.length; j++) {
                            if (stack[j] === nb) {
                                idx = j;
                                break;
                            }
                        }
                        if (idx !== -1) {
                            const cyclePath = [];
                            for (let k = idx; k < stack.length; k++) {
                                cyclePath.push(stack[k]);
                            }
                            cycles.push(cyclePath);
                        }
                    }
                    // BLACK → do nothing
                }
            }
            stack.pop();
            state.set(node, BLACK);
        };
        // Run DFS from all nodes
        const keys = Array.from(adj.keys());
        for (let i = 0; i < keys.length; i++) {
            const node = keys[i];
            if (state.get(node) === WHITE) {
                dfs(node);
            }
        }
        // Deduplicate cycles using canonical rotation
        const unique = [];
        const seen = new Set();
        for (const c of cycles) {
            if (c.length === 0) {
                continue;
            }
            const nodes = c.slice();
            let minIdx = 0;
            for (let i = 1; i < nodes.length; i++) {
                if (nodes[i] < nodes[minIdx]) {
                    minIdx = i;
                }
            }
            const rot = [...nodes.slice(minIdx), ...nodes.slice(0, minIdx)];
            const key = rot.join('->');
            if (!seen.has(key)) {
                seen.add(key);
                unique.push(rot);
            }
        }
        return unique;
    }
    /**
     * Compute topological order using Kahn's algorithm.
     * Only valid when no cycles exist in the graph.
     *
     * @returns {string[]} The nodes in topological order.
     */
    computeTopologicalOrder() {
        const indeg = new Map();
        const keys = Array.from(this.adjacency.keys());
        for (let i = 0; i < keys.length; i++) {
            indeg.set(keys[i], 0);
        }
        const entries = this.adjacency.entries();
        let entryResult = entries.next();
        while (!entryResult.done) {
            const pair = entryResult.value;
            const successors = pair[1];
            const successorIter = successors.values();
            let successorResult = successorIter.next();
            while (!successorResult.done) {
                const to = successorResult.value;
                const current = indeg.has(to) ? indeg.get(to) : 0;
                indeg.set(to, current + 1);
                successorResult = successorIter.next();
            }
            entryResult = entries.next();
        }
        const q = [];
        indeg.forEach(function (degree, taskId) {
            if (degree === 0) {
                q.push(taskId);
            }
        });
        const order = [];
        while (q.length) {
            const n = q.shift(); // Non-null assertion: we know q has elements
            order.push(n);
            const neighbors = this.adjacency.get(n);
            if (neighbors) {
                const iterator = neighbors.values();
                let iterNext = iterator.next();
                while (!iterNext.done) {
                    const nb = iterNext.value; // This is exactly what you had
                    const currentDegree = indeg.has(nb) ? indeg.get(nb) : 0;
                    indeg.set(nb, currentDegree - 1);
                    if (indeg.get(nb) === 0) {
                        q.push(nb);
                    }
                    // Advance to next item
                    iterNext = iterator.next();
                }
            }
        }
        return order;
    }
}
